//
// Confluence i18n and l10n
//  - https://confluence.comsysto.com/pages/viewpage.action?pageId=127832565
//
// Custom Hook to work with currencies and amounts locale aware
//  - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat
//

import { currencyToSymbolMap } from './currencyToSymbolMap'
import { Currency, CurrencyConversionDto } from '../types'
import { retrieveCurrencyConversionRate } from './currencyConversionUtil'

type LocaleNumberFormat = 'decimal' | 'currency'

type useAmountAndCurrencyType = {
  getThousandSeparator: (numberFormat?: LocaleNumberFormat) => string
  getDecimalSeparator: (numberFormat?: LocaleNumberFormat) => string
  formatCurrency: (value: number, currency: string, displayCurrency?: boolean) => string
  calculateAndFormatNewTargetAmount: (
    targetAmount: number,
    targetCurrency: string,
    conversionRate?: number,
    displayCurrency?: boolean,
  ) => string
  calculateAmountInNewCurrency: (
    startingCurrency: Currency,
    targetCurrency: Currency,
    conversionData: CurrencyConversionDto[],
    amount?: number,
  ) => number | undefined
  formatDecimal: (value: number) => string | number
  formatAmountFromString: (amount: string) => string | undefined
  formatCurrencySymbol: (currency: string) => string
  formatAmountAsPercentage: (amount: number, displayDecimals?: boolean, showSymbol?: boolean) => string
  formatPercentageSymbol: () => string
  parseUserInput: (amount: string | undefined) => number | string
  laParseUserInput: (userInput: string | undefined, numberFormat?: LocaleNumberFormat, decimals?: number) => string | number
  laFormatUserInput: (userInput: string | number | undefined, numberFormat?: LocaleNumberFormat) => string
}

export const useAmountAndCurrency = (locale = 'en-US'): useAmountAndCurrencyType => {
  const localeSafe = [sanitizeLocaleForNumberFormat(locale), 'en-US']

  const formatCurrency = (value: number, currency: string, displayCurrency = true): string => {
    const currencySymbol = replaceCurrencyWithSymbol(currency)
    const numberFormat = new Intl.NumberFormat(localeSafe, {
      style: 'currency',
      currency: currency,
    })

    const localeCurrencySymbol = numberFormat.formatToParts(10000.01).filter(({ type }) => type === 'currency')[0].value

    return displayCurrency
      ? numberFormat.format(value).replace(isLocaleCurrencySymbolToBeReplaced(currency) ? localeCurrencySymbol : currency, currencySymbol)
      : numberFormat.format(value).replace(localeCurrencySymbol, '').trim()
  }

  // Useful when objective currency and employee current contract currency differ
  const calculateAndFormatNewTargetAmount = (
    targetAmount: number,
    targetCurrency: string,
    conversionRate?: number,
    displayCurrency = true,
  ): string => {
    if (conversionRate === undefined) {
      const currencySymbol = replaceCurrencyWithSymbol(targetCurrency)
      return `? ${currencySymbol}`
    }
    return formatCurrency(Number((targetAmount * conversionRate).toFixed(2)), targetCurrency, displayCurrency)
  }

  const calculateAmountInNewCurrency = (
    startingCurrency: Currency,
    targetCurrency: Currency,
    conversionData: CurrencyConversionDto[],
    amount?: number,
  ): number | undefined => {
    if (!amount) {
      return undefined
    }
    if (startingCurrency === targetCurrency) {
      return amount
    }
    const conversionRate: number | undefined = retrieveCurrencyConversionRate(startingCurrency, targetCurrency, conversionData)
    if (!conversionRate) {
      return undefined
    }
    return Number((amount * conversionRate).toFixed(2))
  }

  const formatDecimal = (value: number): string | number => {
    if (Number.isNaN(value)) {
      return Number.NaN
    }

    return new Intl.NumberFormat(localeSafe, { style: 'decimal' }).format(value)
  }

  // EXPECTS: Number with DOT as decimal separtor AND two decimals AND NO thousand separators!
  //          Or for nerds: [1-9][0-9]+[.][0-9]{2}
  // WHY?     This is what comes from AntDesign NumberFormat InputField - REGARDELESS what the locale ist!
  //
  // We need to format a String like `60000.00` to `60.000,00` for e.g. locale de_DE
  // Should also do this:
  //  50      => 50
  //  50.00   => 50,00
  //  5000.00 => 5.000,00
  const formatAmountFromString = (amount: string): string | undefined => {
    if (amount === undefined || amount === null) return undefined
    let postFix = ''
    let preFix = ''
    if (amount.includes('.')) {
      const splittedAmount = amount.split('.')
      preFix = splittedAmount[0]
      postFix = getDecimalSeparator() + splittedAmount[1]
    } else {
      preFix = amount
    }
    if (preFix.length > 3) {
      const preFixReversed = preFix.split('').reverse()
      const preFixWithThousands: string[] = []
      let counter = 0
      for (const preFixReversedDigit of preFixReversed) {
        preFixWithThousands.push(preFixReversedDigit)
        if (counter === 2) {
          preFixWithThousands.push(getThousandSeparator())
          counter = 0
        } else {
          counter = counter + 1
        }
      }
      preFix = preFixWithThousands.reverse().join('')
    }
    return preFix + postFix
  }

  const formatCurrencySymbol = (currency: string): string => {
    const currencySymbol = replaceCurrencyWithSymbol(currency)
    const numberFormat = new Intl.NumberFormat(localeSafe, {
      style: 'currency',
      currency: currency,
    })
    const localeCurrencySymbol = numberFormat.formatToParts(10000.01).filter(({ type }) => type === 'currency')[0].value
    return localeCurrencySymbol.replace(isLocaleCurrencySymbolToBeReplaced(currency) ? localeCurrencySymbol : currency, currencySymbol)
  }

  const formatAmountAsPercentage = (amount: number, displayDecimals = true, showSymbol = true): string => {
    let str = new Intl.NumberFormat(localeSafe, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: displayDecimals ? 2 : 0,
    }).format(amount)
    if (!showSymbol) {
      str = str.replace('%', '')
    }
    return str
  }

  const formatPercentageSymbol = (): string => {
    const str = new Intl.NumberFormat(localeSafe, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(0)
    return str.replace(/0/, '').trim()
  }

  const parseUserInput = (amount: string | undefined): number | string => {
    const separator = getDecimalSeparator()
    if (amount === undefined) return ''
    amount = `${amount}`.replace(/[^0-9.,]/g, '')

    switch (separator) {
      case ',':
        amount = amount.replace(/\./g, '').replace(/,/g, '.')
        break
      default:
        amount = amount.replace(/,/g, '')
    }
    const toFixedFloor = (num: string, fixed: number) => {
      if (!num || num.length === 0) {
        return num
      }
      const re = new RegExp('^-?\\d+(?:.\\d{0,' + (fixed || -1) + '})?')
      const match = num.match(re)
      return match ? match[0] : ''
    }
    return toFixedFloor(amount, 2) // makes it so there the last decimals are not rounded up
  }

  // LOCALE AWARE
  const laParseUserInput = (
    userInput: string | undefined,
    numberFormat: LocaleNumberFormat = 'decimal',
    decimals?: number,
  ): string | number => {
    if (userInput === null || userInput === undefined || userInput === '') {
      return ''
    }

    const decimalSeparator = getDecimalSeparator(numberFormat)
    const thousandSeparator = getThousandSeparator(numberFormat)

    let parsedValue = userInput
      .replace(new RegExp(`[^0-9${decimalSeparator}]`, 'g'), '')
      .replace(new RegExp(`\\${thousandSeparator}`, 'g'), '')

    if (decimalSeparator === ',') {
      parsedValue = parsedValue.replace(decimalSeparator, '.').replace(new RegExp(`\\${decimalSeparator}`, 'g'), '')
    } else {
      const matchMultipleDots = parsedValue.match(/.*\..*\..*/g)

      if (!!matchMultipleDots) {
        const indices = [...parsedValue].reduce((acc: number[], current: string, index: number) => {
          if (current === '.') {
            acc.push(index)
          }
          return acc
        }, [])

        if (indices.length > 1) {
          for (let i = indices.length - 1; i > 0; i--) {
            parsedValue = parsedValue.substring(0, indices[i]) + '' + parsedValue.substring(indices[i] + parsedValue.length)
          }
        }
      }
    }

    const matchDecimals = parsedValue.match(new RegExp('^-?\\d+(?:.\\d{0,' + (decimals || -1) + '})?'))

    return !!matchDecimals ? matchDecimals[0] : ''
  }

  // LOCALE AWARE
  const laFormatUserInput = (userInput: string | number | undefined, numberFormat: LocaleNumberFormat = 'decimal'): string => {
    if (userInput === undefined || userInput === null || userInput === '') {
      return ''
    }

    let formattedValue = userInput
    const decimalSeparator = getDecimalSeparator(numberFormat)

    const splitDecimal = String(formattedValue).split('.')

    if (splitDecimal.some((s) => Number.isNaN(Number(s)))) {
      return ''
    }

    formattedValue = Number(splitDecimal[0]).toLocaleString(localeSafe, {
      style: numberFormat,
      currency: numberFormat === 'currency' ? Currency.Eur : undefined,
    })

    if (numberFormat === 'currency') {
      formattedValue = formattedValue
        .substr(0, formattedValue.indexOf(decimalSeparator))
        .replace(replaceCurrencyWithSymbol(Currency.Eur), '')
        .trim()
    }

    if (splitDecimal.length > 1) {
      formattedValue = formattedValue + decimalSeparator + splitDecimal[1]
    }

    return formattedValue
  }

  // should return "," for de_DE
  //            or "." for en_EN
  const getDecimalSeparator = (numberFormat: LocaleNumberFormat = 'decimal'): string => {
    return _getSeparator('decimal', numberFormat)
  }

  // should return "." for de_DE
  //            or "," for en_EN
  const getThousandSeparator = (numberFormat: LocaleNumberFormat = 'decimal'): string => {
    return _getSeparator('group', numberFormat)
  }

  const _getSeparator = (separatorType: string, numberFormat: LocaleNumberFormat): string => {
    const formatter = new Intl.NumberFormat(localeSafe, {
      style: numberFormat,
      currency: numberFormat === 'currency' ? Currency.Eur : undefined,
    })
    return formatter.formatToParts(10000.01).filter(({ type }) => type === separatorType)[0].value
  }

  const replaceCurrencyWithSymbol = (currency: string): string => {
    const currencySymbol = currencyToSymbolMap.get(currency)
    if (currencySymbol) {
      return currencySymbol
    }
    return currency
  }

  return {
    getThousandSeparator,
    getDecimalSeparator,
    formatCurrency,
    calculateAndFormatNewTargetAmount,
    calculateAmountInNewCurrency,
    formatDecimal,
    formatAmountFromString,
    formatCurrencySymbol,
    formatAmountAsPercentage,
    formatPercentageSymbol,
    parseUserInput,
    laParseUserInput,
    laFormatUserInput,
  }
}

//
// HELPERS
//

// IN: de_DE => OUT: de-DE
const sanitizeLocaleForNumberFormat = (localeString: string): string => {
  const sanitized = localeString.replace('_', '-')
  try {
    new Intl.NumberFormat(sanitized)
  } catch (e) {
    return 'en-US'
  }
  if (sanitized === 'de') {
    return 'de-DE'
  }
  if (sanitized === 'en') {
    return 'en-US'
  }
  return sanitized
}

// returns true if for that particular currency you want to use symbol in currencyToSymbolMap.ts
// instead of the one given by Intl
const isLocaleCurrencySymbolToBeReplaced = (currency: string): boolean => {
  if (currency === 'CNY') {
    return true
  }
  return false
}
